/**
 * Strike 3 Phase B — Admin Intelligence Dashboard with 6 sub-sections.
 * - Demo Mode card (moved from Settings)
 * - Skills Demand (per-industry filter)
 * - Coaching Effectiveness (by topic + by coach)
 * - AXIS ↔ Skills Correlations
 * - Assessment Insights (powered by Capsule 2)
 * - AI Provider Health (last 7 days)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Brain, TrendingUp, Activity, Zap, Target, AlertTriangle, Server, Database, ChevronRight, Sparkles } from "lucide-react";
import { DemoModeCard } from "@/components/admin/AdminSettingsStrike3";

const API = process.env.REACT_APP_BACKEND_URL;
const INDUSTRIES = ["", "Automotive", "FMCG", "Technology", "Pharma", "Financial Services", "Manufacturing", "Consulting", "Healthcare", "Education", "Other"];

function StatBox({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-navy/5 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-dm text-navy/50 text-xs uppercase tracking-wider">{label}</span>
        <Icon className="w-4 h-4 text-gold" />
      </div>
      <p className="font-playfair text-navy text-3xl font-bold leading-none">{value}</p>
      {hint && <p className="font-dm text-navy/40 text-[10px] mt-1">{hint}</p>}
    </div>
  );
}

function SectionCard({ title, subtitle, icon: Icon, children, action }) {
  return (
    <div className="bg-white rounded-xl shadow-md border border-navy/5 p-5" data-testid={`intel-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          {Icon && <Icon className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <h3 className="font-playfair text-navy text-lg font-bold">{title}</h3>
            {subtitle && <p className="font-dm text-navy/50 text-xs mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ msg = "No data yet — once users complete CapScans / coaching sessions, this section will populate." }) {
  return <p className="font-dm text-navy/40 text-xs text-center py-6 italic">{msg}</p>;
}

function ZoneBadge({ value }) {
  if (value == null) return <span className="text-navy/30">—</span>;
  const color = value >= 2 ? "bg-red-100 text-red-700" : value >= 1 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700";
  return <span className={`font-dm text-[10px] font-bold px-2 py-0.5 rounded-full ${color}`}>{value > 0 ? `+${value}` : value}</span>;
}

function SuccessBadge({ pct }) {
  if (pct == null) return <span>—</span>;
  const color = pct >= 95 ? "text-green-600" : pct >= 80 ? "text-amber-600" : "text-red-600";
  return <span className={`font-dm font-bold ${color}`}>{pct.toFixed(1)}%</span>;
}

export default function IntelligenceDashboard() {
  const [overview, setOverview] = useState({});
  const [skills, setSkills] = useState({ rows: [] });
  const [skillsIndustry, setSkillsIndustry] = useState("");
  const [coaching, setCoaching] = useState({ by_topic: [], by_coach: [] });
  const [correlations, setCorrelations] = useState({ rows: [] });
  const [assessment, setAssessment] = useState({ rows: [] });
  const [providers, setProviders] = useState({ providers: [], recent_errors: [] });
  const [seeding, setSeeding] = useState(false);

  const loadAll = async () => {
    try {
      const [ov, ci, corr, ass, prov] = await Promise.all([
        axios.get(`${API}/api/admin/intelligence/overview`, { withCredentials: true }),
        axios.get(`${API}/api/admin/intelligence/coaching-effectiveness`, { withCredentials: true }),
        axios.get(`${API}/api/admin/intelligence/correlations`, { withCredentials: true }),
        axios.get(`${API}/api/admin/intelligence/assessment-insights`, { withCredentials: true }),
        axios.get(`${API}/api/admin/intelligence/provider-health`, { withCredentials: true }),
      ]);
      setOverview(ov.data);
      setCoaching(ci.data);
      setCorrelations(corr.data);
      setAssessment(ass.data);
      setProviders(prov.data);
    } catch (err) { console.error(err); }
  };

  const loadSkills = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/intelligence/skills-demand`, { params: skillsIndustry ? { industry: skillsIndustry } : {}, withCredentials: true });
      setSkills(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadSkills(); }, [skillsIndustry]);

  const seed = async () => {
    if (!window.confirm("Seed ~60 synthetic intelligence rows to preview the dashboard?")) return;
    setSeeding(true);
    try {
      await axios.post(`${API}/api/admin/intelligence/seed-sample`, {}, { withCredentials: true });
      await loadAll();
      await loadSkills();
    } catch (err) { alert(err?.response?.data?.detail || "Seed failed"); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-6" data-testid="intelligence-dashboard">
      {/* Demo Mode (relocated from Settings) */}
      <DemoModeCard />

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatBox icon={Database} label="Skill rows" value={overview.skills_intelligence ?? 0} hint="CapScan-derived" />
        <StatBox icon={Brain} label="Coaching rows" value={overview.coaching_intelligence ?? 0} hint="Sessions analysed" />
        <StatBox icon={Target} label="Correlations" value={overview.behaviour_gap_correlation ?? 0} hint="AXIS ↔ CapScan" />
        <StatBox icon={Sparkles} label="Assessments" value={overview.assessment_intelligence ?? 0} hint="Response-level" />
        <StatBox icon={Server} label="AI calls (all-time)" value={overview.api_usage ?? 0} hint="Across all providers" />
      </div>

      {/* Skills Demand */}
      <SectionCard title="Skills Demand" icon={TrendingUp} subtitle="Most-requested skills + average gap. Filter by industry to spot sector-specific patterns."
        action={(
          <select value={skillsIndustry} onChange={e => setSkillsIndustry(e.target.value)} data-testid="skills-industry-filter"
            className="border border-navy/15 rounded-lg px-3 py-1.5 font-dm text-xs">
            {INDUSTRIES.map(i => <option key={i || "all"} value={i}>{i || "All industries"}</option>)}
          </select>
        )}>
        {skills.rows.length === 0 ? <EmptyState /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/10">
                  <th className="text-left font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Skill</th>
                  <th className="text-left font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Dimension</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Appearances</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Avg gap</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Critical (≥2)</th>
                </tr>
              </thead>
              <tbody>
                {skills.rows.map((r, i) => (
                  <tr key={i} className="border-b border-navy/5">
                    <td className="font-dm text-navy text-sm font-semibold py-2 px-2">{r.skill}</td>
                    <td className="font-dm text-navy/60 text-xs py-2 px-2">{r.dimension}</td>
                    <td className="font-dm text-navy text-sm text-right py-2 px-2">{r.appearances}</td>
                    <td className="text-right py-2 px-2"><ZoneBadge value={r.avg_gap} /></td>
                    <td className="font-dm text-navy text-sm text-right py-2 px-2">{r.critical_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Coaching Effectiveness */}
      <SectionCard title="Coaching Effectiveness" icon={Brain} subtitle="Confidence shift (end − start) per topic and per coach persona.">
        {coaching.by_topic.length === 0 && coaching.by_coach.length === 0 ? <EmptyState /> : (
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <p className="font-dm text-navy/50 text-[10px] uppercase font-bold mb-2">By topic category</p>
              <div className="space-y-1.5" data-testid="coaching-by-topic">
                {coaching.by_topic.slice(0, 8).map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="font-dm text-navy">{r.topic_category || "Uncategorised"}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-dm text-navy/40 text-xs">{r.sessions} sessions</span>
                      <span className="font-dm font-bold text-green-600">+{r.avg_delta}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="font-dm text-navy/50 text-[10px] uppercase font-bold mb-2">By coach persona</p>
              <div className="space-y-1.5" data-testid="coaching-by-coach">
                {coaching.by_coach.slice(0, 8).map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="font-dm text-navy capitalize">{r.coach_persona}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-dm text-navy/40 text-xs">{r.actions_pct}% actions</span>
                      <span className="font-dm font-bold text-green-600">+{r.avg_delta}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SectionCard>

      {/* AXIS ↔ Skills Correlations */}
      <SectionCard title="AXIS ↔ Skills Correlations" icon={Target} subtitle="Which behavioural styles correlate with critical CapScan gaps. Insight for hiring + coaching playbooks.">
        {correlations.rows.length === 0 ? <EmptyState /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/10">
                  <th className="text-left font-dm text-navy/50 text-[10px] uppercase py-2 px-2">AXIS dominant style</th>
                  <th className="text-left font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Critical skill</th>
                  <th className="text-left font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Dimension</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Occurrences</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Avg gap</th>
                </tr>
              </thead>
              <tbody>
                {correlations.rows.map((r, i) => (
                  <tr key={i} className="border-b border-navy/5">
                    <td className="py-2 px-2"><span className="font-dm text-[10px] px-2 py-0.5 rounded-full bg-navy/5 text-navy">{r.axis_style}</span></td>
                    <td className="font-dm text-navy text-sm font-semibold py-2 px-2">{r.capscan_skill}</td>
                    <td className="font-dm text-navy/60 text-xs py-2 px-2">{r.dimension || "—"}</td>
                    <td className="font-dm text-navy text-sm text-right py-2 px-2">{r.count}</td>
                    <td className="text-right py-2 px-2"><ZoneBadge value={r.avg_gap} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Assessment Insights */}
      <SectionCard title="Assessment Insights" icon={Sparkles} subtitle="Question-bank performance from the upcoming Assessment Builder (Capsule 2).">
        {assessment.rows.length === 0 ? <EmptyState msg="Will populate after Capsule 2 — Assessment Builder MVP ships." /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/10">
                  <th className="text-left font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Skill</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Responses</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Avg score</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">AI scored</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">AI ↔ Human</th>
                </tr>
              </thead>
              <tbody>
                {assessment.rows.map((r, i) => (
                  <tr key={i} className="border-b border-navy/5">
                    <td className="font-dm text-navy text-sm font-semibold py-2 px-2">{r.skill}</td>
                    <td className="font-dm text-navy text-sm text-right py-2 px-2">{r.responses}</td>
                    <td className="font-dm text-navy text-sm text-right py-2 px-2">{r.avg_score ?? "—"}</td>
                    <td className="font-dm text-navy/60 text-xs text-right py-2 px-2">{r.ai_scored_count}</td>
                    <td className="font-dm font-bold text-right py-2 px-2 text-navy">{r.agreement_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* AI Provider Health */}
      <SectionCard title="AI Provider Health" icon={Activity} subtitle="Last 7 days — success rate, latency, token usage per provider in the fallback chain."
        action={(
          <button onClick={seed} disabled={seeding} data-testid="seed-sample-btn"
            className="bg-cream border border-navy/15 text-navy font-dm font-bold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-navy/5 disabled:opacity-50">
            <Zap className="w-3 h-3" /> {seeding ? "Seeding…" : "Seed sample data"}
          </button>
        )}>
        {providers.providers.length === 0 ? <EmptyState msg="No AI calls in the last 7 days." /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/10">
                  <th className="text-left font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Provider · Model</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Calls</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Success rate</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Avg latency</th>
                  <th className="text-right font-dm text-navy/50 text-[10px] uppercase py-2 px-2">Tokens (in/out)</th>
                </tr>
              </thead>
              <tbody>
                {providers.providers.map((p, i) => (
                  <tr key={i} className="border-b border-navy/5">
                    <td className="font-dm text-navy text-sm py-2 px-2">
                      <span className="font-semibold">{p.provider}</span> · <span className="text-navy/60 text-xs">{p.model}</span>
                    </td>
                    <td className="font-dm text-navy text-sm text-right py-2 px-2">{p.total_calls}</td>
                    <td className="text-right py-2 px-2"><SuccessBadge pct={p.success_rate_pct} /></td>
                    <td className="font-dm text-navy/70 text-xs text-right py-2 px-2">{p.avg_latency_ms} ms</td>
                    <td className="font-dm text-navy/60 text-xs text-right py-2 px-2">{p.tokens_in}/{p.tokens_out}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {providers.recent_errors?.length > 0 && (
          <div className="mt-5 pt-4 border-t border-navy/10" data-testid="provider-errors-section">
            <p className="font-dm text-navy/50 text-[10px] uppercase font-bold mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500" /> Recent errors</p>
            <div className="space-y-1.5">
              {providers.recent_errors.slice(0, 5).map((e, i) => (
                <div key={i} className="font-dm text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  <span className="font-semibold text-red-700">{e.provider}/{e.model}</span>
                  <span className="text-navy/40 ml-2">{e.created_at ? new Date(e.created_at).toLocaleString("en-IN") : ""}</span>
                  <p className="text-navy/70 mt-0.5 truncate">{e.error}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
